import type { IHostToolDefinition } from '../gpthelp-host-contract';
import { adminCardFormat } from '../cards/admin-card-format';
import { HostToolError } from './tool-types';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpTool,
  IGpthelpToolGate,
  IGpthelpToolTurn,
} from './tool-types';
import { isRecord } from './tool-args';
import { ensureScopeKeyOrThrow } from './tool-scope';
import { buildGpthelpToolGate } from './tool-gate';
import { toolOffered } from './tool-switch';
import { localizeUtcInstantsDeep } from './tool-localize';
import { isToolResultEnvelope } from './tool-envelope';
import { assistantTools } from './domains/assistant-tools';
import { exportTools } from './domains/export-tools';
import { menuTools } from './domains/menu-tools';
import { statsTools } from './domains/stats-tools';
import { cajaTools } from './domains/caja-tools';
import { daycloseTools } from './domains/dayclose-tools';
import { ordersTools } from './domains/orders-tools';
import { tablesTools } from './domains/tables-tools';
import { stockTools } from './domains/stock-tools';
import { clubTools } from './domains/club-tools';
import { couponsTools } from './domains/coupons-tools';
import { customersTools } from './domains/customers-tools';
import { reservationsTools } from './domains/reservations-tools';
import { storeTools } from './domains/store-tools';

// ===== Explicitly out of scope (decided, do not add) =====
// Reservation turno configuration (arrival interval per turno, fixed arrival
// times, special-date intervals): setup data with no support value; the
// resolved day windows in reservations_day already answer "when do they open".
// Realtime stats (endpoint orphaned — no admin UI consumer); payments list
// (covered by orders_get movements + stats_paymentMethods + day closes);
// table/sector config WRITES and floor-plan geometry (tables_list covers read
// parity; geometry has no support value); live tables board (client-derived,
// no admin REST endpoint — occupancy questions are answered by orders_find
// with pending statuses); waiter calls; sent-emails log; POS devices page;
// the printers, ticket and sticker setup and a product's ticket split
// (comandaSplit, not in the printing section plan §8 R1 defines; which
// kitchen station prints which categories and products is
// store_getSettings' printing section, below); AFIP/invoicing setup config;
// delivery-zone geometry (polygons and coordinates: no support value);
// discounts config; reporting-categories config (visible through
// stats_categories); billing/plan (context effectiveTier suffices);
// translations; customer WRITE operations
// (see Writes below); member filter on club_getMovements
// (the server has none — filter per member via club_getMember); full-menu
// bulk series on /stats/item-options/series (explicit optionItemIds <= 20
// keeps the payload bounded; rankings come from /stats/item-options).
// Parameter-level exclusions (verified dead or redundant): orderTypes on
// stats_tips (the /stats/tips handler never reads a types query param — dead
// code end-to-end, exposing it would be a silent no-op that misleads the
// model); tables[] filter on orders_find (free-text search already matches
// table names); customer lat/lng and the linked user relation (PII with no
// support value — dropped by sanitizeCustomerRow). QR generation page (/qr): action-only
// surface (client-side rendering of posters/stickers); the table list it
// shows is already covered by tables_list.
// Writes (propose-and-yield): no model-called tool writes. Menu changes are
// covered by menu_proposeChanges (availability, also turning on in this
// branch only a product that is off in every branch; price and price
// raise/lower by percent or amount, where a product with a discounted price
// needs the user's answer on that discount first, enforced by the Action
// API; the discounted price of a product or size, set or removed; many
// different prices at once on Plus; rename; description; create, also with
// a discounted price, hidden, or in a category the same proposal creates;
// categories: create, rename, description, their own show/hide switch
// (here or in every branch) and «Solo en el Punto de Venta»; moving
// products to another category of the same menu; the order of a category's
// products and of a menu's categories; option groups, shared ones included:
// create one (also as a copy of another for only some products), its name,
// description and rules, link it to products, take it off them or reorder a
// product's groups, add, change (also the price and discounted price, with
// the same discount answer), pause, remove and reorder its choices; a
// product's variants (sizes): add them (also turning a product with one
// price into variants), rename, pause, remove (all of them = back to one
// price) and reorder them, a switch with a discounted price needing the same
// discount answer; the hours of products and of categories (Plus); a copy of
// a product, created hidden in every branch (a member over every branch);
// preparation notes), each
// kind of change only while the Action API offers it to the member
// (`/config` ops): it only
// prepares an inert proposal that the user applies with a tap on the card,
// through the Action API, and can undo for 7 days. Its card also says what
// still hides a product or category it turns on (its category, menu or
// hours, from the same menu store the read tools use, so no API read), which
// products show a change of an option group (the Action API's `affected`,
// also given to the model as counts) and, when nothing was prepared, why
// each product or category was left out.
// Inventory changes of this branch are covered by stock_proposeChanges, its
// own proposal kind (Plus, and CatalogManage in the branch, like every stock
// page): a purchase, a loss or a physical count (the movements the stock
// modal writes and the stock stats show), stock items created or edited
// (name, unit, cost, minimum stock), and the recipe of a product, a variant
// or an option choice (replaced, or removed). It is the same inert proposal,
// applied from its card and undone for 7 days (a movement by an opposite
// one); a request with menu and inventory changes calls both write tools in
// one round, and each proposal keeps its own card. Its card says that only
// this branch's inventory changes, reads every quantity in its stock item's
// unit, and lists the products that use the option group of a choice whose
// recipe changes (the Action API's `affected`); its receipt opens the stock
// page instead of the Menú, and «Cambios del asistente» lists it with the
// menu's changes.
// Everything else stays out:
// deletes of products, categories or option groups (there is no restore
// endpoint; a group is taken off its products instead), deletes of stock
// items and other movement types (no restore primitive; the stock page
// writes only purchases and adjustments), per-product group
// rules and «Ocultar en este producto» (the V2 editor never writes them; a
// copy keeps a product's own rules), pictures, translations, allergens, VAT
// and printers (the
// handoff card of ui_navigate opens the exact editor and lists the page's
// steps instead); settings writes (WhatsApp, hours, ordering modes, payment
// methods: their endpoints overwrite whole forms); customer records; orders,
// payments, caja, AFIP/invoicing, billing and members/roles (money, legal or
// access: the assistant explains and offers the page instead); account email
// and password changes (support_handoff writes the email request for the
// user to send to our team; a password is reset with 'Forgot my password').
// The option library (the Opciones page list) is menu_getOptionGroups, for
// the members the page's API serves (CatalogManage): every menu at once
// (each group's menuId stands in for the page's menu filter), paused choices
// included, with the products that use each group. Choice pictures stay out
// (no support value). The menu as a file (products, sizes, prices,
// availability here, options) is menu_exportCatalog, built from the same menu
// store the read tools use.
// The settings pages are store_getSettings, read-only, one section per call,
// each section gated like the page that shows it: ordering (order types, how
// orders arrive, the payment methods of each order type and whether online
// payments are linked), hours, delivery (cost, minimum, zone names and
// fees), printing (the kitchen stations and the categories and products
// that print at each), branches (the ones the member works in; address and
// city only for members who open «Sucursales») and team (each member's role,
// permissions and branches). Their writes stay out (see Writes), and so do
// credentials (branch tokens, Wi-Fi passwords, Mercado Pago data, mail
// settings), contact data of members and branches (emails, phones, WhatsApp
// numbers), user ids, coordinates and a store contact section (contact data
// nobody asked for). The club program's rules are in club_summary (the
// owner's terms clamped); program writes stay out (the points customers earn
// and spend are money). Sales of every active branch the member works in (up
// to 20) are stats_orders with allBranches.
// support_handoff only shows a message the user sends to our team, for what
// only the team can do (account or login problems, the owner or the login
// email, closing the store or the account, plan or billing, a bug); the
// assistant never contacts the team itself.
// The Action API's other reads (`AssistantService`): `GET assistant/config`
// decides which tools and ops are offered (writes on or off, and the ops and
// host tools switched off for this member, read again after 5 minutes), the
// welcome and the support link (the model sees their effect, not the
// switches; only the support card carries the link);
// `POST assistant/identity-token` is a credential; `GET proposals/:id` and
// its refresh are the proposal the card shows (the model gets only its id
// and row count, so it never lists rows).
// The activity (`GET assistant/actions`) is assistant_listChanges.
// Reservations: all writes, including the batch table seats/mode write
// (`PUT /reservations/tables`, `ReservationsService.saveTables`) and the
// branch contact patch (`BranchesService.updateContact`) and table creation
// from the guide (`POST /tables`, `TablesService.createTable`) — writes;
// administrative settings, shifts (including their
// fixed arrival times), date overrides and table groups (configuration, not
// operational data, for the support agent; reservations_day already
// exposes the resolved windows of a date); operational booking-context/slot
// inventory (supports booking mutations only). Reservation guest email/notes
// and account links are withheld from reservations_guests.
// reservations_day/find/guests/stats cover the operational read surface; the
// upcoming and pending-count projections and the no-show confirmation route
// are views/mutations of the same rows (no tool).
// Store setup and AI menu import (`StoreSetupService`, `MenuImportsService`,
// `/store-setup/*`, `/menu-imports/*`): transient onboarding state and a
// pre-publication draft; everything the owner keeps afterwards is regular
// catalog, branch and restaurant data the existing tools already read (the
// checklist is derived from it). The orders save is a write. The live refresh
// of an update from photos, its undo and `menu.lastUpdate` are the same: a
// transient session and a 7-day undo pointer; the catalog they change is
// already readable.
// `restaurants.business_type` is shown nowhere after creation.
// Menú page (catalog V2) writes beyond the menu_proposeChanges ops: bulk
// delete, `DELETE /v2/categories`, the fields no op writes (pictures,
// translations, a variant's picture and description, the variants' title)
// and
// `PATCH /restaurants/:id/settings` (the Descuentos switch, a settings write);
// the menu tools read the same menu store the page writes to.
// `GET /v2/items/full/r/:rid?ids=`: an incremental refresh of catalog data the
// menu tools already read.
// Storefront look («Diseño y estilo»): `PUT /restaurants/:id/design` (style,
// brand colour, mode, banner focal point) is a settings write; the saved look
// is read through restaurant.design. The «Lo más pedido» switch
// (`PATCH /restaurants/:id/settings` { showPopular }) is a settings write like
// Descuentos; catalog.popularShowcase reads it. The storefront's style catalog
// (`GET <website>/__storefront/styles`) is static data the design semantics
// already name. The banner focal point (`restaurant.bannerMeta.focalX/focalY`,
// drawn by the Diseño page's crop outline) and the logo's suggested colour
// (`restaurant.logoColor`, the «Del logo» swatch) are layout and suggestion
// aids with no support value: the agent reads the resulting look through
// restaurant.design and points the owner to that page for both.
// =========================================================

/**
 * Registration order defines the order tools are offered to the model.
 * gpthelp-api silently drops every tool past its 64-tool limit, so the
 * assistant's own tools go first and the rarely needed export goes last.
 */
const ALL_TOOLS: IGpthelpTool[] = [
  ...assistantTools,
  ...menuTools,
  ...statsTools,
  ...cajaTools,
  ...daycloseTools,
  ...ordersTools,
  ...tablesTools,
  ...stockTools,
  ...clubTools,
  ...couponsTools,
  ...customersTools,
  ...reservationsTools,
  ...storeTools,
  ...exportTools,
];

const TOOLS_BY_NAME: Map<string, IGpthelpTool> = (() => {
  const map = new Map<string, IGpthelpTool>();
  for (const tool of ALL_TOOLS) {
    if (map.has(tool.definition.name)) {
      throw new Error(`[gpthelp] Duplicate tool name: ${tool.definition.name}`);
    }
    map.set(tool.definition.name, tool);
  }
  return map;
})();

export function buildCartadigitalAdminHostToolsV1(
  gate: IGpthelpToolGate
): IHostToolDefinition[] {
  if (!gate.hasRestaurantScope) return [];
  return ALL_TOOLS.filter((tool) => toolOffered(tool, gate)).map(
    (tool) => tool.definitionFor?.(gate) ?? tool.definition
  );
}

/** The turn of a request that came without one (tests, older widgets). */
const NO_TURN: IGpthelpToolTurn = {
  conversationId: '',
  turnId: '',
  requestId: '',
  tainted: false,
  userText: '',
};

export async function executeCartadigitalAdminTool(params: {
  name: string;
  args: unknown;
  deps: ICartadigitalAdminHostToolDeps;
  /**
   * Scope the turn was asked in (echoed by TOOL_REQUEST). Required: a request
   * without it (null, or absent in widgets older than host contract v2), or
   * for another restaurant/branch, never runs.
   */
  scopeKey: string | null | undefined;
  /** The widget's turn (host contract §3.2). */
  turn?: IGpthelpToolTurn;
}): Promise<unknown> {
  const { name, args, deps, scopeKey } = params;

  if (!isRecord(args)) {
    throw new HostToolError('INVALID_ARGS', 'Tool args must be an object', {
      args,
    });
  }

  // Last line of defence: the widget already rejects requests whose scope
  // differs from its current prop, but the stores are the source of truth.
  const scope = ensureScopeKeyOrThrow(deps, scopeKey);

  const tool = TOOLS_BY_NAME.get(name);
  if (!tool) {
    throw new HostToolError('TOOL_NOT_FOUND', 'Unknown tool name', { name });
  }

  // Permissions, role, branch settings and the tool switches can change after
  // the tools of this turn were offered: re-check with a gate built from the
  // current stores.
  const gate = buildGpthelpToolGate(deps);
  if (!toolOffered(tool, gate)) {
    throw new HostToolError(
      'NOT_AVAILABLE',
      'This tool is not available to the current user in the selected store and branch. Tell the user they cannot see this information here.',
      { name }
    );
  }

  const result = await tool.execute(args, {
    deps,
    scope,
    gate,
    turn: params.turn ?? NO_TURN,
    fmt: adminCardFormat(deps, scope.timeZone),
  });

  // Every instant the model sees is branch-local (see _semantics.time in the
  // host context). Arguments are not localized: tool-args parses instants
  // with `new Date(raw)`, which accepts the ±HH:MM strings the model echoes.
  // Cards carry preformatted text: only an envelope's data is localized.
  if (isToolResultEnvelope(result)) {
    return {
      ...result,
      data: localizeUtcInstantsDeep(result.data, scope.timeZone),
    };
  }
  return localizeUtcInstantsDeep(result, scope.timeZone);
}
